import React from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaUsers, FaShieldAlt, FaMapMarkedAlt } from 'react-icons/fa';
import './ContactUs.css';

// Import Assets
import contactHero from '../../Assets/contact.png'; // Using existing contact image

const ContactUs = () => {

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="contact-page-wrapper">

      {/* 1. HERO SECTION */}
      <section className="contact-hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="display-4 fw-bold mb-3 text-dark">
                  Get in Touch With <br />
                  <span className="text-black">Janta Roadways</span>
                </h1>
                <p className="text-muted lead">
                  Partner with a trusted leader in automobile and heavy cargo logistics
                  across India. Our team is ready to support your transport requirements.
                </p>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <img src={contactHero} alt="Janta Roadways Logistics" className="img-fluid rounded-4 shadow-lg w-100" />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 2. OFFICES SECTION */}
      <section className="offices-section py-5 bg-white">
        <Container>
          <div className="text-center mb-5">
            <h6 className="text-uppercase text-muted fw-bold small">Our Offices</h6>
          </div>
          <Row>
            {/* Corporate Office */}
            <Col md={4} className="mb-4">
              <motion.div
                className="office-card p-4 h-100 border-top-red shadow-sm"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <h6 className="fw-bold text-uppercase mb-4">Corporate Office – Chennai</h6>

                <div className="d-flex mb-3">
                  <FaMapMarkerAlt className="text-red me-3 mt-1" />
                  <p className="small text-muted mb-0">
                    D2, Thyagaraja Complex <br />
                    Kilpauk, Chennai – 600010 <br />
                    Tamil Nadu, India
                  </p>
                </div>

                <div className="d-flex mb-3">
                  <FaPhoneAlt className="text-red me-3 mt-1" />
                  <p className="small text-muted mb-0">+91 XXXXX XXXXX</p>
                </div>

                <div className="d-flex">
                  <FaEnvelope className="text-red me-3 mt-1" />
                  <p className="small text-muted mb-0">info@jantaroadways.com</p>
                </div>
              </motion.div>
            </Col>

            {/* Operations Office */}
            <Col md={4} className="mb-4">
              <motion.div
                className="office-card p-4 h-100 border-top-red shadow-sm"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: 0.2 }}
              >
                <h6 className="fw-bold text-uppercase mb-4">Operations Office – Bangalore</h6>

                <div className="d-flex mb-3">
                  <FaMapMarkerAlt className="text-red me-3 mt-1" />
                  <p className="small text-muted mb-0">
                    Dabaspet, Bengaluru <br />
                    Karnataka, India
                  </p>
                </div>

                <div className="d-flex mb-3">
                  <FaPhoneAlt className="text-red me-3 mt-1" />
                  <p className="small text-muted mb-0">+91 XXXXX XXXXX</p>
                </div>

                <div className="d-flex">
                  <FaEnvelope className="text-red me-3 mt-1" />
                  <p className="small text-muted mb-0">info@jantaroadways.com</p>
                </div>
              </motion.div>
            </Col>

            {/* Branch Offices */}
            <Col md={4} className="mb-4">
              <motion.div
                className="office-card p-4 h-100 border-top-red shadow-sm"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: 0.4 }}
              >
                <h6 className="fw-bold text-uppercase mb-4">Branch Offices</h6>
                <ul className="list-unstyled small text-muted ps-2">
                  <li className="mb-2">▪ Pune</li>
                  <li className="mb-2">▪ Ranchi</li>
                  <li className="mb-2">▪ Becharaji</li>
                  <li className="mb-2">▪ Manesar</li>
                </ul>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 3. STRENGTHS SECTION */}
      <section className="strengths-section py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h6 className="text-red text-uppercase fw-bold small">Our Strengths</h6>
            <h2 className="fw-bold display-6">Driving Reliable Logistics Across India</h2>
          </div>

          <Row>
            {[
              { icon: FaUsers, title: "Dedicated Logistics Team", desc: "Our experienced operations team ensures smooth coordination from pickup to delivery." },
              { icon: FaShieldAlt, title: "Transparent Communication", desc: "Real-time updates and direct contact with operations managers." },
              { icon: FaMapMarkedAlt, title: "Pan-India Presence", desc: "Strong network coverage across major industrial and automotive hubs." }
            ].map((item, idx) => (
              <Col md={4} key={idx} className="mb-4">
                <motion.div
                  className="strength-card bg-white p-4 rounded shadow-sm h-100 text-center" // Text center to match typical strength layout
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  viewport={{ once: true}}
                >
                  <div className="icon-box1 text-red mb-3" style={{textAlign:"start"}}>
                    <item.icon size={40} />
                  </div>
                  <h5 className="fw-bold mb-3" style={{textAlign:"start"}}>{item.title}</h5>
                  <p className="text-muted small mb-0" style={{textAlign:"start"}}>{item.desc}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

   {/* 4. FORM SECTION */}
<section className="contact-form-section py-5">
    <Container>
        {/* Main Dark Wrapper */}
        <div className="outer-contact-wrapper rounded-4 overflow-hidden shadow-lg">
            <Row className="g-0">
                {/* Form Side (White Card with Border Effect) */}
                <Col lg={7} className="form-card-container">
                    <div className="bg-white p-5 h-100 form-inner-box">
                        <h3 className="fw-bold mb-4">Get In touch</h3>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Control type="text" placeholder="Full name" className="custom-input" />
                            </Form.Group>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Control type="email" placeholder="Email" className="custom-input" />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Control type="text" placeholder="Phone number" className="custom-input" />
                                </Col>
                            </Row>
                            <Form.Group className="mb-4">
                                <Form.Control as="textarea" rows={4} placeholder="Example Text" className="custom-input" />
                            </Form.Group>
                            <Button className="btn-red-submit px-4 py-2 fw-bold text-uppercase">
                                Request a Call Back
                            </Button>
                        </Form>
                    </div>
                </Col>

                {/* Info Side (Dark) */}
                <Col lg={5} className="p-5 d-flex flex-column justify-content-center bg-brand-dark text-white">
                    <h2 className="display-5 fw-bold mb-4">Speak With Our <br /> Logistics Experts</h2>
                    <p className="opacity-75 mb-5 text-description">
                        Whether you require automobile transport, chassis movement, ODC handling,
                        or mining logistics — our team is ready to assist you with tailored solutions.
                    </p>

                    <div className="contact-info mt-auto">
                        <div className="d-flex align-items-center mb-3">
                            <FaPhoneAlt className="me-3 text-white" />
                            <span className="fw-semibold">+91 XXXXX XXXXX</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <FaEnvelope className="me-3 text-white" />
                            <span className="fw-semibold">info@jantaroadways.com</span>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    </Container>
</section>

    </div>
  );
};

export default ContactUs;
